// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Button, Divider, Group, Loader, Paper, Stack, Text, Title } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { normalizeErrorString } from '@medplum/core';
import { QuestionnaireResponseDisplay, useMedplum } from '@medplum/react';
import type { JSX } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { fetchPatientScreenerEntries, type ScreenerExportEntry } from '../../../utils/patient-export';

export function IntakeScreenerPage(): JSX.Element {
  const { patientId, encounterId } = useParams();
  const medplum = useMedplum();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState<ScreenerExportEntry[]>([]);

  const load = useCallback(async () => {
    if (!patientId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      setEntries(await fetchPatientScreenerEntries(medplum, patientId));
    } catch (err) {
      showNotification({ color: 'red', message: normalizeErrorString(err), autoClose: false });
    } finally {
      setLoading(false);
    }
  }, [medplum, patientId]);

  useEffect(() => {
    load().catch(console.error);
  }, [load]);

  return (
    <Stack gap="lg" p="md" maw={860} mx="auto">
      <Group justify="space-between">
        <Title order={2}>Intake Screener</Title>
        <Button
          variant="subtle"
          onClick={() =>
            navigate(
              encounterId ? `/Patient/${patientId}/Encounter/${encounterId}` : `/Patient/${patientId}/medical-info`
            )
          }
        >
          {encounterId ? 'Back to Encounter Note' : 'Back to Medical Info'}
        </Button>
      </Group>

      {loading && (
        <Stack align="center" py="xl">
          <Loader size="sm" />
        </Stack>
      )}

      {!loading && entries.length === 0 && (
        <Paper withBorder radius="md" shadow="sm" p="xl">
          <Text c="dimmed">No intake screener responses found.</Text>
        </Paper>
      )}

      {!loading &&
        entries.map(({ questionnaire, latestQR }) => (
          <Paper key={questionnaire.id} withBorder radius="md" p="xl">
            <Title order={3} mb="sm">
              {questionnaire.title ?? questionnaire.name}
            </Title>
            <Divider mb="md" />
            <QuestionnaireResponseDisplay questionnaireResponse={latestQR} />
          </Paper>
        ))}
    </Stack>
  );
}
